import React, { ChangeEvent, FC, useCallback, useContext, useState } from 'react'
import { useDispatch } from 'react-redux'
import { DatePicker } from 'components/forms/DatePicker'
import moment from 'moment-timezone'
import { FormModal } from 'transfix-ui/components/Modals'
import { TimeInput, buildDateTime } from 'transfix-ui/components/TimeInput'
import { Box, Grid, Typography } from 'transfix-ui/core'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { UserContext } from 'apps/fleet-planner/context/UserContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { ILoad } from 'apps/fleet-planner/types/loads'
import { updateLoadStatus } from 'apps/fleet-planner/utils/actions/loadDetails'
import { FORMATS } from 'constants/datetime'
import { DISPLAY } from 'constants/display'

interface IPickupEtaModalProps {
  load: ILoad
  isOpen: boolean
  onClose: () => void
}

export const PickupEtaModal: FC<IPickupEtaModalProps> = ({ isOpen, load, onClose }) => {
  const { trackEvent } = useFMSAnalytics()
  const { timeFormats } = useContext(UserContext)
  const timeFormat = timeFormats?.stopTimeInput
  const [error, setError] = useState<string | undefined>(undefined)
  const [date, setDate] = useState<string>('')
  const [time, setTime] = useState<string>('')
  const dispatch = useDispatch()

  const submitHandler = async () => {
    const pickupEta = buildDateTime(date, time, timeFormat)
    if (!pickupEta?.isValid()) return setError('Please provide all missing timestamps.')
    trackEvent('Load', 'DISPATCHER_PICKUP_ETA_FORM_SUBMIT')
    try {
      setError(undefined)
      const eta = pickupEta ? pickupEta.toISOString() : ''
      dispatch(updateLoadStatus(load, LOAD_STATE.Dispatched, undefined, eta))
      return onClose()
    } catch (e) {
      setError(e)
      return undefined
    }
  }

  const dateChangeHandler = useCallback((dates: string[]) => {
    if (!dates[0]) {
      setDate('')
    } else {
      setDate(moment(dates[0]).format(FORMATS.flatpickrDateTimeFormat))
    }
  }, [])

  const timeChangeHandler = useCallback(({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setTime(value)
  }, [])

  return (
    <FormModal
      error={error}
      size={4}
      title='Confirm your ETA to continue'
      open={isOpen}
      onClose={onClose}
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
        },
        {
          label: 'Submit',
          type: 'confirm',
          action: submitHandler,
        },
      ]}>
      <Typography variant='body2' component='div'>
        Please provide your driver&apos;s ETA to the pickup
      </Typography>
      <div className='mt4 mb3'>
        <label className='label required' htmlFor='check-in'>
          Pickup ETA
        </label>
        <Grid container>
          <Grid item>
            <Box display='flex'>
              <Box>
                <DatePicker
                  date={date}
                  placeholder={DISPLAY.emptyDateTime}
                  onDateChange={dateChangeHandler}
                  large
                />
              </Box>
              <Box pl={1}>
                <TimeInput
                  timeFormat={timeFormat}
                  placeholder={DISPLAY.emptyDateTime}
                  value={time}
                  onChange={timeChangeHandler}
                  large
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </div>
    </FormModal>
  )
}
